import {DataTable} from '../ui/DataTable';
import {formatDateOnly} from '../dates';
import './repair-board-approvals.css';
import {StatusBadge} from '../ui/StatusBadge';
import { alphaSave } from '../ui/alpha-save';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, authenticatedFetch } from '../api';
import { ErrorState, Loading } from '../ui/State';

type Decision = {
  id: string;
  title: string;
  summary: string;
  requestedAction: string;
  status: string;
  version: number;
  dueOn: string | null;
  canRespond: boolean;
  response: { decision: string; rationale: string } | null;
  board?:{requestId:string;approved:number;required:number;total:number;proposal:{scope:string;gross:string;supplier:string}}|null;
  documents: { id: string; versionId: string; title: string }[];
};
export function DirectorDecisions() {
  const [selectedId,setSelectedId]=useState<string>();
  const [items, setItems] = useState<Decision[]>(),
    [error, setError] = useState<unknown>();
  const load = () =>
    api<{ items: Decision[] }>('/api/portal/director-decisions').then((r) => setItems(r.items));
  useEffect(() => {
    let active = true;
    api<{ items: Decision[] }>('/api/portal/director-decisions')
      .then((r) => {
        if (active) setItems(r.items);
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
    };
  }, []);
  const selected=items?.find(item=>item.id===selectedId)??items?.[0];
  return (
    <div className="director-decisions">
      <h1>Your decisions</h1>
      <p>
        Review the supporting documents before responding. Your response is retained with this
        version of the request.
      </p>
      {error ? (
        <ErrorState error={error} />
      ) : !items ? (
        <Loading />
      ) : items.length === 0 ? (
        <p>No decisions are currently shared with you.</p>
      ) : (
        <><DataTable caption={<span className="sr-only">Decisions explicitly shared with this account</span>} scrollLabel="Shared decision register"
 rows={items} pageSize={18} getRowKey={item=>item.id}
 rowHeader={{id:'decision',header:'Decision',render:item=><button className="register-record-link" aria-pressed={selected?.id===item.id} onClick={()=>setSelectedId(item.id)}>{item.title}</button>}} columns={[
 {id:'action',header:'Requested action',render:item=>item.requestedAction},
 {id:'due',header:'Due',className:'portal-decision-due',render:item=>formatDateOnly(item.dueOn,'Not recorded')},
 {id:'state',header:'Recorded state',render:item=><StatusBadge text={item.board?(item.board.approved>=item.board.required?'Board majority reached':'Awaiting board majority'):item.status}/>},
 ]}/>{selected&&<DecisionCard key={selected.id} item={selected} reload={load}/>}</>
      )}
    </div>
  );
}
function DecisionCard({ item, reload }: { item: Decision; reload: () => Promise<void> }) {
  const [decision, setDecision] = useState('Approved'),
    [reason, setReason] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(),
    [saved, setSaved] = useState(false);
  const pending = useRef<{
    decision: string;
    reason: string;
    expectedVersion: number;
    idempotencyKey: string;
  } | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    pending.current ??= {
      decision,
      reason,
      expectedVersion: item.version,
      idempotencyKey: crypto.randomUUID(),
    };
    try {
      await alphaSave(pending, '/api/portal/director-decisions/' + item.id + '/respond', {
        method: 'POST',
        body: JSON.stringify(pending.current),
      });
      await reload();
      pending.current = null;
      setSaved(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function download(version: string) {
    setError(undefined);
    try {
      const r = await authenticatedFetch(
        `/api/portal/director-decisions/${item.id}/documents/${version}`,
      );
      if (!r.ok) throw Error('This document is unavailable. Ask your manager to review access.');
      const url = URL.createObjectURL(await r.blob()),
        a = document.createElement('a');
      a.href = url;
      a.download = 'decision-evidence';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e);
    }
  }
  return (
    <section className="panel director-decision">
      <h2>{item.title}</h2>
      <p>{item.summary}</p>
      <p>
        <strong>Requested action:</strong> {item.requestedAction}
      </p>
      <p>
        <StatusBadge text={item.board?(item.board.approved>=item.board.required?'Board majority reached':'Awaiting board majority'):item.status}/>
        {item.dueOn ? <span className="director-decision__deadline">Respond by {formatDateOnly(item.dueOn)}</span> : null}
      </p>
      {item.board&&<p>{item.board.approved} approvals received; {item.board.required} affirmative votes required from {item.board.total} directors.</p>}
      {item.board&&<section className="repair-board-proposal" aria-label="Exact repair proposal"><h3>Exact repair proposal</h3>
       <p>{item.board.proposal.supplier} · £{item.board.proposal.gross} including VAT</p>
<p>{item.board.proposal.scope}</p>
</section>}
      <ul className="director-decision__evidence" aria-label="Supporting documents">
        {item.documents.map((d) => (
          <li key={d.versionId}>
            <button type="button" onClick={() => void download(d.versionId)}>
              {d.title}
            </button>
          </li>
        ))}
      </ul>
      {item.response && (
        <p>
          <strong>{item.board?'Your vote — ':''}{item.response.decision}:</strong> {item.response.rationale}
        </p>
      )}
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {saved && <p role="status">Your response has been saved.</p>}
      {item.canRespond && (
        <form className="form" onSubmit={submit}>
          <label>
            Response
            <select
              disabled={busy || !!pending.current}
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
            >
              <option>Approved</option>
              <option>Rejected</option>
              <option>Changes requested</option>
            </select>
          </label>
          <label>
            Reason or requested changes
            <textarea
              required
              minLength={10}
              maxLength={2000}
              disabled={busy || !!pending.current}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <button disabled={busy}>
            {busy ? 'Saving…' : pending.current ? 'Retry the same response' : 'Save response'}
          </button>
        </form>
      )}
    </section>
  );
}
